/**
 * Parse and validate AWS Textract AnalyzeID response JSON, then map fields to blocks.
 * @see https://docs.aws.amazon.com/textract/latest/dg/API_AnalyzeID.html
 */

import { z } from "zod";

const AnalyzeIdNormalizedValueSchema = z.object({
  Value: z.string(),
  ValueType: z.string(),
});

const AnalyzeIdFieldDetectionSchema = z.object({
  Text: z.string(),
  Confidence: z.number().optional(),
  NormalizedValue: AnalyzeIdNormalizedValueSchema.optional(),
});

const AnalyzeIdIdentityDocumentFieldSchema = z.object({
  Type: AnalyzeIdFieldDetectionSchema,
  ValueDetection: AnalyzeIdFieldDetectionSchema,
});

const AnalyzeIdGeometrySchema = z.object({
  BoundingBox: z.object({
    Height: z.number(),
    Left: z.number(),
    Top: z.number(),
    Width: z.number(),
  }),
  Polygon: z.array(
    z.object({
      X: z.number(),
      Y: z.number(),
    })
  ),
});

const AnalyzeIdBlockSchema = z.object({
  BlockType: z.string(),
  Geometry: AnalyzeIdGeometrySchema,
  Id: z.string(),
  Text: z.string().default(""),
  // Other fields omitted as they aren't needed for current use cases
});
type AnalyzeIdBlock = z.infer<typeof AnalyzeIdBlockSchema>;

export const AnalyzeIdResponseSchema = z.object({
  AnalyzeIDModelVersion: z.string(),
  DocumentMetadata: z.object({
    Pages: z.number(),
  }),
  IdentityDocuments: z.array(
    z.object({
      Blocks: z.array(AnalyzeIdBlockSchema),
      DocumentIndex: z.number(),
      IdentityDocumentFields: z.array(AnalyzeIdIdentityDocumentFieldSchema),
    })
  ),
});

export type Polygon = {
  x: number;
  y: number;
}[];

export const NAME_FIELD_TYPES = ["FIRST_NAME", "MIDDLE_NAME", "LAST_NAME"];
export const ADDRESS_FIELD_TYPES = [
  "ADDRESS",
  "CITY_IN_ADDRESS",
  "STATE_IN_ADDRESS",
  "ZIP_CODE_IN_ADDRESS",
];
export const FIELD_TYPES_TO_CONSIDER = [
  ...NAME_FIELD_TYPES,
  ...ADDRESS_FIELD_TYPES,
  "DATE_OF_BIRTH",
];

/**
 * Find the best Block for the extracted value: (1) first exact normalized match,
 * else (2) first block among those with maximum longest common substring length
 * (length ≥ 2), else undefined. WORD blocks before LINE; ties keep scan order.
 */
function findBlockForValue(
  blocks: AnalyzeIdBlock[],
  valueText: string
): AnalyzeIdBlock | undefined {
  const valueNorm = normalizeText(valueText);
  if (!valueNorm) return undefined;

  const wordBlocks = blocks.filter((b) => b.BlockType === "WORD");
  const lineBlocks = blocks.filter((b) => b.BlockType === "LINE");
  const ordered = [...wordBlocks, ...lineBlocks];

  let bestLen = 0;
  let bestBlock: AnalyzeIdBlock | undefined;

  for (const block of ordered) {
    const blockNorm = normalizeText(block.Text);
    if (blockNorm === valueNorm) return block; // 1. Exact match
    const len = blockNorm
      ? longestCommonSubstringLength(blockNorm, valueNorm)
      : 0;
    if (len >= 2 && len > bestLen) {
      bestLen = len;
      bestBlock = block;
    }
  }
  return bestBlock; // 2. Longest common substring match
}

function normalizeText(s: string): string {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Longest contiguous substring shared by `a` and `b` (character-wise). */
function longestCommonSubstringLength(a: string, b: string): number {
  if (!a.length || !b.length) return 0;
  const n = a.length;
  const m = b.length;
  let best = 0;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0)
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) {
        const v = dp[i - 1]![j - 1]! + 1;
        dp[i]![j] = v;
        if (v > best) best = v;
      }
    }
  }
  return best;
}

/**
 * Convert block geometry (normalized 0–1) to pixel polygon for DocumentViewer highlights.
 */
function blockToPolygon(
  block: AnalyzeIdBlock,
  docWidth: number,
  docHeight: number
): Polygon | null {
  const geom = block.Geometry;
  const poly = geom.Polygon;
  // Use polygon if available, otherwise use bounding box
  if (poly && poly.length >= 3) {
    return poly.map((p) => ({
      x: p.X * docWidth,
      y: p.Y * docHeight,
    }));
  }
  return boundingBoxToPolygon(geom.BoundingBox, docWidth, docHeight);
}

function boundingBoxToPolygon(
  box: { Left: number; Top: number; Width: number; Height: number },
  width: number,
  height: number
): Polygon {
  const left = box.Left * width;
  const top = box.Top * height;
  const w = box.Width * width;
  const h = box.Height * height;
  return [
    { x: left, y: top },
    { x: left + w, y: top },
    { x: left + w, y: top + h },
    { x: left, y: top + h },
  ];
}

/**
 * Parse and validate AnalyzeID response JSON, then build fields and highlights.
 */
export interface ParsedAnalyzeIdResult {
  id: string;
  type: string;
  value: string;
  normalizedValue?: string;
  confidence: number;
  polygon: Polygon | null;
}
export function parseAnalyzeIdResponse(
  rawResults: Record<string, unknown>,
  documentWidth: number,
  documentHeight: number
): ParsedAnalyzeIdResult[] {
  const results: ParsedAnalyzeIdResult[] = [];

  const parsed = AnalyzeIdResponseSchema.safeParse(rawResults);
  if (!parsed.success) return results;
  const { data } = parsed;
  // TODO: Handle if there's more than 1 document page
  const firstDoc = data.IdentityDocuments[0];
  if (!firstDoc) {
    return results;
  }
  const fields = firstDoc.IdentityDocumentFields;
  const blocks = firstDoc.Blocks;

  fields.forEach((field, index) => {
    const typeText = field.Type.Text;
    if (!FIELD_TYPES_TO_CONSIDER.includes(typeText)) {
      return;
    }
    const valueText = field.ValueDetection.Text;
    const normalizedValue = field.ValueDetection.NormalizedValue?.Value;
    const confidence = field.ValueDetection.Confidence ?? 0;
    const id = `field-${index}-${typeText}`;

    const block = findBlockForValue(blocks, valueText);
    const polygon: Polygon | null = block
      ? blockToPolygon(block, documentWidth, documentHeight)
      : null;

    results.push({
      id,
      type: typeText,
      value: valueText,
      normalizedValue,
      confidence,
      polygon,
    });
  });

  return results;
}
